// src/components/NewsletterSignup.jsx
// "Subscribe to our newsletter" form, shared by the site footer and the
// Contact Us page. Saves the address to the `newsletter_subscribers`
// Firestore collection -- same create-only pattern the other public forms
// on this site use (see pages/EnquiryForm.jsx for `enquiries`), with the
// field names as the contract: email / source / page / status / createdAt.
// Repeat sign-ups are written again rather than looked up first, because a
// read on this collection isn't something a visitor is allowed to do.
import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { FaRegEnvelope, FaCheckCircle, FaArrowRight } from "react-icons/fa";
import { db } from "../firebase";
import "./NewsletterSignup.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * @param {"footer"|"panel"} variant  dark footer strip, or light page card
 * @param {string} source             where the sign-up came from, stored as-is
 * @param {string} title              heading above the field
 * @param {string} description        one line under the heading
 */
const NewsletterSignup = ({
  variant = "footer",
  source = "footer",
  title = "Subscribe to our newsletter",
  description = "New products, engineering notes and offers — about once a month. No spam.",
}) => {
  const [email, setEmail] = useState("");
  // Honeypot -- hidden from people, filled in by naive bots. Anything in
  // here silently drops the submission.
  const [website, setWebsite] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (website) {
      setSubscribed(true);
      return;
    }

    const value = email.trim();
    if (!value) {
      setError("Please enter your email address.");
      return;
    }
    if (!EMAIL_RE.test(value)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, "newsletter_subscribers"), {
        email: value.toLowerCase(),
        status: "subscribed",
        source,
        page: window.location.pathname,
        createdAt: serverTimestamp(),
      });
      setSubscribed(true);
      setEmail("");
    } catch (err) {
      console.error("Newsletter subscription error:", err);
      setError(
        "We couldn't sign you up just now. Please try again, or email info@bibaelectronics.com."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`ns-block ns-${variant}`}>
      <div className="ns-text">
        <h3 className="ns-title">
          <FaRegEnvelope className="ns-title-icon" aria-hidden="true" />
          {title}
        </h3>
        <p className="ns-desc">{description}</p>
      </div>

      {subscribed ? (
        <p className="ns-success" role="status">
          <FaCheckCircle className="ns-success-icon" aria-hidden="true" />
          You&apos;re on the list — thanks for subscribing.
        </p>
      ) : (
        <form className="ns-form" onSubmit={handleSubmit} noValidate>
          <div className="ns-field">
            <label className="ns-label" htmlFor={`ns-email-${variant}`}>
              Email address
            </label>
            <input
              id={`ns-email-${variant}`}
              className={`ns-input${error ? " ns-input-error" : ""}`}
              type="email"
              name="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              aria-invalid={error ? "true" : "false"}
              aria-describedby={error ? `ns-error-${variant}` : undefined}
              disabled={submitting}
            />

            {/* Honeypot -- hidden from people, not from bots. */}
            <input
              className="ns-honeypot"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              aria-hidden="true"
            />
          </div>

          <button className="ns-btn" type="submit" disabled={submitting}>
            {submitting ? "Subscribing…" : "Subscribe"}
            {!submitting && <FaArrowRight className="ns-btn-icon" aria-hidden="true" />}
          </button>

          {error && (
            <span className="ns-error" id={`ns-error-${variant}`} role="alert">
              {error}
            </span>
          )}
        </form>
      )}
    </div>
  );
};

export default NewsletterSignup;
